// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type {
  AccountInfo,
  ClearCacheRequest,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { isAccountForConfiguredAuthority } from "../../authConfig";
import { logAuthFailure } from "../../utils/authDiagnostics";

export interface PrepareSessionOptions {
  bootedOnAuthSurface?: () => boolean;
  isSignOutRedirect?: () => boolean;
}

export const SIGN_IN_CALLBACK_PATH = "/signin-callback";

export function bootedOnSignInCallback(): boolean {
  return window.location.pathname === SIGN_IN_CALLBACK_PATH;
}

export function isSignOutRedirectFromLocation(
  instance: IPublicClientApplication,
): boolean {
  if (!bootedOnSignInCallback()) {
    return false;
  }
  return instance.getAllAccounts().length === 0;
}

export async function prepareCachedSession(
  instance: IPublicClientApplication,
  options: PrepareSessionOptions = {},
): Promise<void> {
  const bootedOnAuthSurface = options.bootedOnAuthSurface ?? bootedOnSignInCallback;
  const isSignOutRedirect =
    options.isSignOutRedirect ?? (() => isSignOutRedirectFromLocation(instance));

  const cachedAccounts = instance
    .getAllAccounts()
    .filter(isAccountForConfiguredAuthority);
  const account: AccountInfo | null = cachedAccounts[0] ?? null;
  if (account === null) {
    return;
  }

  let signOutRedirect = false;
  try {
    signOutRedirect = bootedOnAuthSurface() && isSignOutRedirect();
  } catch {
    signOutRedirect = false;
  }

  try {
    instance.setActiveAccount(account);
  } catch (error) {
    logAuthFailure("session-prepare-set-account-failed", error);
  }

  if (signOutRedirect) {
    const clearRequest: ClearCacheRequest = { account };
    try {
      await instance.clearCache(clearRequest);
    } catch (error) {
      logAuthFailure("session-prepare-clear-cache-failed", error);
    }
  }
}
